import { useMediaListFilters } from "@/components/news-feed/news-feed-filters/publisher-media-list-filter/useMediaListFilters";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { DISCOVERED_TAG_CATEGORIES } from "@/lib/constants/discovered-tag-categories";
import { useFeature } from "@/lib/feature-management";
import { useDynamicFilters } from "@/store/news-feed/filters/useDynamicFilters";
import { useAnalysisParamsContext } from "@/store/news-feed/params/analysis-params.context";

const LIST_CATEGORY = "PUBLISHER_MEDIA_LIST_ID" as const;
const GROUP_CATEGORY = "PUBLISHER_MEDIA_LIST_GROUP_ID" as const;

/**
 * Prefetches {@link useDynamicFilters} data for URL-selected dynamic categories.
 * Mount on the Analysis route so applied-filter pills resolve when the sidebar is collapsed.
 * No-op when `fetch-dynamic-filters-exp` is off.
 */
const usePrefetchSelectedFilterCategories = () => {
  const fetchOnExpand = useFeature("fetch-dynamic-filters-exp", false);
  const discoveredTagsHidden = useFeature("discovered-tags-hidden", false);
  const enableArticleTagging = useFeature("enable-article-tagging", false);
  const analysis = useAnalysisParamsContext();
  const selectedFilters = analysis?.selectedFilters;

  const shouldPrefetch = (category: ShareVoiceSidebarCategory) =>
    fetchOnExpand &&
    (!discoveredTagsHidden || !DISCOVERED_TAG_CATEGORIES.has(category)) &&
    (selectedFilters?.[category]?.length ?? 0) > 0;

  const shouldPrefetchArticleTags =
    enableArticleTagging &&
    (selectedFilters?.FEED_ARTICLE_TAG?.length ?? 0) > 0;

  useDynamicFilters("AUTHOR", shouldPrefetch("AUTHOR"));
  useDynamicFilters("EVENT", shouldPrefetch("EVENT"));
  useDynamicFilters("INITIATIVE", shouldPrefetch("INITIATIVE"));
  useDynamicFilters("NARRATIVE_CLUSTER", shouldPrefetch("NARRATIVE_CLUSTER"));
  useDynamicFilters("PEOPLE", shouldPrefetch("PEOPLE"));
  useDynamicFilters("PUBLISHER", shouldPrefetch("PUBLISHER"));
  useDynamicFilters("THEME", shouldPrefetch("THEME"));
  useDynamicFilters("TOPIC", shouldPrefetch("TOPIC"));
  useDynamicFilters("FEED_ARTICLE_TAG", shouldPrefetchArticleTags);
};

/** Runs {@link useMediaListFilters} for the prefetch subtree only. */
const PrefetchSelectedMediaListFiltersLoader = () => {
  useMediaListFilters();
  return null;
};

/**
 * Prefetches publisher media list filters when the URL has list or group selections.
 * Rendered conditionally so {@link useMediaListFilters} is not mounted when
 * `fetch-dynamic-filters-exp` is off (that hook always fetches in that case).
 */
const PrefetchSelectedMediaListFilters = () => {
  const fetchOnExpand = useFeature("fetch-dynamic-filters-exp", false);
  const analysis = useAnalysisParamsContext();
  const selectedFilters = analysis?.selectedFilters;

  const hasSelectedMediaLists =
    (selectedFilters?.[LIST_CATEGORY]?.length ?? 0) > 0 ||
    (selectedFilters?.[GROUP_CATEGORY]?.length ?? 0) > 0;

  if (!fetchOnExpand || !hasSelectedMediaLists) {
    return null;
  }

  return <PrefetchSelectedMediaListFiltersLoader />;
};

/**
 * Analysis-route prefetch for dynamic filter pill labels (sidebar may be unmounted).
 * @example `<PrefetchSelectedFilterCategories />` in `AnalysisControlPanel`
 */
export const PrefetchSelectedFilterCategories = () => {
  usePrefetchSelectedFilterCategories();
  return <PrefetchSelectedMediaListFilters />;
};
